import {
  ProviderUnavailableError,
  type FrameInterpolationProvider,
  type FrameInterpolationRequest,
  type FrameInterpolationResult,
  type ProviderCallOptions,
} from "@loumy/contracts";
import { streamFalStatus } from "./fal-status-stream";
import { safeFetchBytes } from "./safe-fetch";

// §7 `frame-interpolation`-Provider (D94) → FILM (Frame Interpolation for Large Motion)
// über die fal.ai-Queue-API — glättet die Spritesheet-Loop-Naht: 1–2 ML-interpolierte
// Zwischenframes zwischen dem NEUEN End-Frame (nach dem Loop-Cut) und dem Start-Frame.
// Gleicher FAL_API_KEY wie video/image/vision/image-edit (EIN fal-Vertrag).
// D108: kein Build pinnt diesen Slot mehr — die `loop`-Capability ist wieder aktiv, aber
// Cut-only (kein FILM); FILM nutzt nur noch der Pin-gated Alt-Spec-Naht-Pfad (D94/D99,
// alte executed_pipeline_specs/Resume — D32-Muster). Registrierung bleibt dafür bestehen.
//
// Modell-Contract `fal-ai/film` (fal.ai/models/fal-ai/film/api, verifiziert 2026-06-04):
//   start_image_url (REQUIRED), end_image_url (REQUIRED), num_frames (int, Zwischenframes),
//   output_type ∈ images|video (wir: images), image_format ∈ png|jpeg (wir: png — verlustfrei,
//   die Frames werden downstream chroma-gekeyt), include_start/include_end (bool — wir: false,
//   NUR die Zwischenframes; Start/Ende existieren bereits in der Frame-Sequenz).
//   Output (images): { images: [{ url }] } in zeitlicher Reihenfolge start→end.
//
// Einzelaufruf — wie FalQwenImageEditProvider (image-edit-provider.ts): bewusst OHNE
// D27-Handle-Resume; `opts.signal` bricht ab. D106: Status PRIMÄR über den SSE-STREAM
// (EIN Subrequest bis zum terminalen Status, fal-status-stream.ts) — das frühere
// 1,5-s-Polling addierte sich mit den übrigen Knoten über das Subrequest-Limit der
// Worker-Invocation (D104-Befund). Fallback (Stream nicht verfügbar/abgebrochen) = Polling
// mit 5-s-Intervall (User-Spec: „realtime wenn möglich, sonst alle 5 s").

const FAL_QUEUE_BASE_URL = "https://queue.fal.run";
const FAL_FILM_MODEL = "fal-ai/film";
const POLL_INTERVAL_MS = 5000; // D106: Fallback-Polling — 5 s statt 1,5 s
const MAX_POLLS = 36; // Fallback-Deckel bleibt ~3 min (36 × 5 s); FILM ist real deutlich schneller
// Größen-Cap je Ergebnis-PNG (DoS-/Speicher-Schutz beim Download): Frames sind Video-
// Auflösung (≤1080p) — 16 MB ist großzügiger Headroom.
const MAX_FRAME_BYTES = 16 * 1024 * 1024;
// D94: maximale Zwischenframe-Zahl, die wir anfordern (Spec: 1–2; Cap defensiv).
const MAX_NUM_FRAMES = 4;

export interface FalFilmConfig {
  apiKey?: string;
  baseUrl?: string;
  /** Modell-Override (Default `fal-ai/film`). */
  model?: string;
}

/** sleep, das auf opts.signal reagiert (Cancel/Timeout bricht sofort ab). */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new Error("aborted"));
    const t = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(t);
        reject(new Error("aborted"));
      },
      { once: true },
    );
  });
}

/**
 * §7 frame-interpolation → FILM über fal.ai (D94). `interpolate({startImageUrl, endImageUrl,
 * numFrames})` → submit → poll bis COMPLETED → Ergebnis-PNGs herunterladen (safeFetchBytes,
 * größen-gedeckelt) → Bytes in zeitlicher Reihenfolge.
 */
export class FalFilmProvider implements FrameInterpolationProvider {
  readonly id = "fal-film";
  readonly version = 1;
  readonly displayName = "fal.ai (FILM Frame-Interpolation)";

  private readonly baseUrl: string;
  private readonly model: string;

  constructor(private readonly config: FalFilmConfig = {}) {
    this.baseUrl = (config.baseUrl?.trim() || FAL_QUEUE_BASE_URL).replace(/\/+$/, "");
    this.model = config.model?.trim() || FAL_FILM_MODEL;
  }

  private requireKey(): string {
    if (!this.config.apiKey)
      throw new ProviderUnavailableError("frame-interpolation", this.id, "FAL_API_KEY fehlt (Adapter nicht konfiguriert, §13).");
    return this.config.apiKey;
  }

  private headers(apiKey: string, json: boolean): Record<string, string> {
    const h: Record<string, string> = { Authorization: `Key ${apiKey}` };
    if (json) h["Content-Type"] = "application/json";
    return h;
  }

  /** fal-Queue-URLs gegen den fal-Host validieren (SSRF/Drift, wie die übrigen fal-Adapter). */
  private requireFalUrl(url: unknown, field: string): string {
    if (typeof url !== "string" || url.length === 0) throw new Error(`fal film: '${field}' fehlt.`);
    let host: string;
    try {
      host = new URL(url).origin;
    } catch {
      throw new Error(`fal film: '${field}' ist keine gültige URL.`);
    }
    if (host !== new URL(this.baseUrl).origin)
      throw new Error(`fal film: '${field}' (${host}) liegt nicht auf dem fal-Host (${new URL(this.baseUrl).origin}).`);
    return url;
  }

  /** COMPLETED → Ergebnis-PNGs laden (gemeinsamer Endpfad von Stream- und Poll-Weg, D106). */
  private async fetchResult(
    responseUrl: string,
    apiKey: string,
    numFrames: number,
    requestId: string,
    signal?: AbortSignal,
  ): Promise<FrameInterpolationResult> {
    const resultRes = await fetch(responseUrl, { method: "GET", headers: this.headers(apiKey, false), signal });
    if (!resultRes.ok) throw new Error(`fal film result: HTTP ${resultRes.status} ${resultRes.statusText}`);
    const result = (await resultRes.json()) as { images?: { url?: unknown }[] };
    const urls = (result.images ?? []).map((im) => im?.url).filter((u): u is string => typeof u === "string");
    if (urls.length === 0) throw new Error("fal film: COMPLETED, aber keine images[].url im Ergebnis.");
    // Frame-Anzahl VOR den Downloads deckeln (Codex-P2): ein malformter Provider-Response
    // darf nicht mehr als die angeforderte/gedeckelte Zahl 16-MB-Downloads erzwingen.
    if (urls.length > Math.max(numFrames, 1))
      throw new Error(`fal film: ${urls.length} Frames im Ergebnis, angefordert ${numFrames} (malformter Response).`);
    // Ergebnis-PNGs laden (safeFetchBytes: https-only, redirect-/größen-gedeckelt) —
    // Reihenfolge der API-Antwort = zeitliche Reihenfolge start→end.
    const frames: Uint8Array[] = [];
    for (const url of urls) {
      const { bytes } = await safeFetchBytes(url, { maxBytes: MAX_FRAME_BYTES, signal });
      frames.push(bytes);
    }
    return {
      frames,
      provenance: { provider: this.id, modelVersion: this.model, requestId, seed: null },
    };
  }

  async interpolate(req: FrameInterpolationRequest, opts?: ProviderCallOptions): Promise<FrameInterpolationResult> {
    const apiKey = this.requireKey();
    const signal = opts?.signal;
    const numFrames = Math.max(1, Math.min(MAX_NUM_FRAMES, Math.round(req.numFrames)));

    const submit = await fetch(`${this.baseUrl}/${this.model}`, {
      method: "POST",
      headers: this.headers(apiKey, true),
      body: JSON.stringify({
        start_image_url: req.startImageUrl,
        end_image_url: req.endImageUrl,
        num_frames: numFrames,
        output_type: "images",
        image_format: "png", // verlustfrei — die Naht-Frames werden downstream chroma-gekeyt
        include_start: false, // NUR Zwischenframes: Start/Ende existieren in der Sequenz bereits
        include_end: false,
      }),
      signal,
    });
    if (!submit.ok) throw new Error(`fal film submit: HTTP ${submit.status} ${submit.statusText}`);
    const sub = (await submit.json()) as { request_id?: string; status_url?: unknown; response_url?: unknown };
    if (!sub.request_id) throw new Error("fal film submit: keine request_id in der Antwort.");
    const statusUrl = this.requireFalUrl(sub.status_url, "status_url");
    const responseUrl = this.requireFalUrl(sub.response_url, "response_url");

    // D106 PRIMÄR: SSE-Status-Stream (EIN Subrequest bis zum terminalen Status).
    const terminal = await streamFalStatus({ statusUrl, apiKey, signal });
    if (terminal !== null) {
      if (terminal.error !== undefined) throw new Error(`fal film: ${terminal.error}`);
      if (terminal.status === "COMPLETED") return this.fetchResult(responseUrl, apiKey, numFrames, sub.request_id, signal);
      // Terminal, aber weder COMPLETED noch error-Feld (z.B. FAILED/CANCELLED) — gleiche
      // Bewertung wie der Poll-Pfad (Codex-P2-Semantik).
      throw new Error(`fal film: unerwarteter Status '${terminal.status ?? "—"}' (terminal ohne error-Feld?).`);
    }

    // D106 FALLBACK (Stream nicht verfügbar/Timeout/Netzabbruch): Polling alle 5 s.
    for (let i = 0; i < MAX_POLLS; i++) {
      if (signal?.aborted) throw new Error("fal film: aborted (Job-Timeout/Cancel).");
      const statusRes = await fetch(statusUrl, { method: "GET", headers: this.headers(apiKey, false), signal });
      if (statusRes.ok) {
        const status = (await statusRes.json()) as { status?: string; error?: string };
        if (status.error) throw new Error(`fal film: ${status.error}`);
        if (status.status === "COMPLETED") return this.fetchResult(responseUrl, apiKey, numFrames, sub.request_id, signal);
        // NUR die dokumentierten Pending-Zustände tolerieren (Codex-P2): ein terminaler/unbekannter
        // Status OHNE error-Feld (z.B. FAILED/CANCELLED) darf nicht das volle Poll-Fenster verbrennen.
        if (status.status !== "IN_QUEUE" && status.status !== "IN_PROGRESS")
          throw new Error(`fal film: unerwarteter Status '${status.status ?? "—"}' (terminal ohne error-Feld?).`);
      } else if (statusRes.status !== 429 && statusRes.status < 500) {
        throw new Error(`fal film status: HTTP ${statusRes.status} ${statusRes.statusText}`);
      }
      await sleep(POLL_INTERVAL_MS, signal);
    }
    throw new Error(`fal film: nicht fertig nach ${MAX_POLLS} Polls (~${(MAX_POLLS * POLL_INTERVAL_MS) / 1000}s).`);
  }
}
